// src/components/common/Navbar.js
import React, { useContext } from 'react';
import styled, { keyframes } from 'styled-components';
import { motion } from 'framer-motion';
import { 
  FaBars, 
  FaBell, 
  FaSearch, 
  FaPalette,
  FaEyeSlash
} from 'react-icons/fa';
import { ThemeContext } from '../context/ThemeContext';
import { AuthContext } from '../context/AuthContext';
import { useTranslation } from '../hooks/useTranslation';
import { getPublicAssetUrl } from '../utils/publicAssets';
import RestaurantDropdown from '../components/common/RestaurantDropdown';
import QuickThemeSwitcher from '../components/common/QuickThemeSwitcher';
import LanguageSwitcher from '../components/common/LanguageSwitcher';

const BRAND_LOGO_SRC = getPublicAssetUrl('TheRest360.png');

const NavbarContainer = styled(motion.header)`
  background: linear-gradient(180deg, rgba(15, 15, 15, 0.98) 0%, rgba(20, 20, 20, 0.95) 100%);
  backdrop-filter: blur(30px);
  -webkit-backdrop-filter: blur(30px);
  border-bottom: 1px solid var(--primary-rgba-15);
  padding: 1.25rem 0;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: fixed;
  top: 0;
  z-index: 1000;
  width: 100%;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.6),
              inset 0 -1px 0 rgba(255, 255, 255, 0.03);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  height: auto;
  min-height: 60px;

  /* Subtle gradient overlay */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(90deg,
      var(--primary-rgba-3) 0%,
      transparent 50%,
      var(--primary-rgba-3) 100%
    );
    opacity: 0.5;
    pointer-events: none;
  }

  /* Bottom accent line */
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg,
      transparent 0%,
      var(--primary-rgba-30) 50%,
      transparent 100%
    );
  }
  
  /* Desktop */
  @media (min-width: 1025px) {
    left: ${props => props.isSidebarExpanded ? '250px' : '70px'}; /* Dynamic Offset */
    width: calc(100% - ${props => props.isSidebarExpanded ? '250px' : '70px'});
    padding: 1.25rem 2rem;
  }
  
  /* Tablet */
  @media (min-width: 769px) and (max-width: 1024px) {
    left: 0;
    width: 100%;
    padding: 1.25rem 1.5rem;
    gap: 0.75rem;
  }
  
  /* Mobile */
  @media (max-width: 768px) {
    left: 0;
    width: 100%;
    padding: 0.625rem 0.75rem;
    gap: 0.375rem;
    min-height: 52px;
    
    /* Hide navbar when search is visible on mobile */
    transform: ${props => props.isSearchVisible ? 'translateY(-100%)' : 'translateY(0)'};
    transition: transform 0.3s ease;
    opacity: ${props => props.isSearchVisible ? '0' : '1'};
    pointer-events: ${props => props.isSearchVisible ? 'none' : 'auto'};
  }
  
  /* Small mobile */
  @media (max-width: 375px) {
    padding: 0.5rem 0.625rem;
    gap: 0.25rem;
  }
`;

const NavLeft = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  gap: 0.75rem;
  flex-shrink: 0;
  z-index: 1;

  @media (max-width: 768px) {
    gap: 0.5rem;
  }

  @media (max-width: 375px) {
    gap: 0.25rem;
  }
`;

const wordmarkShimmerLight = keyframes`
  0%, 100% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
`;

const NavBrandGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => (props.$withWordmark ? '0.375rem' : '0')};
  min-width: 0;
`;

const NavBrandWordmark = styled.span`
  font-family: 'Poppins', system-ui, sans-serif;
  font-size: 1.125rem;
  font-weight: 800;
  letter-spacing: -0.03em;
  white-space: nowrap;
  line-height: 1.2;
  flex-shrink: 0;
  background: linear-gradient(
    118deg,
    #f1f5f9 0%,
    #ffffff 24%,
    #5eead4 48%,
    #ffffff 58%,
    #99f6e4 78%,
    #ecfeff 100%
  );
  background-size: 200% auto;
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  filter: drop-shadow(0 1px 1px rgba(0, 0, 0, 0.45));

  @media (prefers-reduced-motion: no-preference) {
    animation: ${wordmarkShimmerLight} 9s ease-in-out infinite;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    background-position: 0% 50%;
  }

  @media (max-width: 1024px) {
    font-size: 1rem;
  }

  @media (max-width: 400px) {
    max-width: 9rem;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const LogoImage = styled.img`
  height: ${props => (props.$withWordmark ? '44px' : '60px')};
  width: auto;
  max-width: ${props => (props.$withWordmark ? '168px' : '400px')};
  margin: 0;
  padding: 0;
  object-fit: contain;
  display: block;
  vertical-align: top;
  flex-shrink: 0;

  @media (max-width: 1024px) {
    height: ${props => (props.$withWordmark ? '36px' : '46px')};
    max-width: ${props => (props.$withWordmark ? '132px' : '300px')};
  }

  @media (min-width: 1025px) {
    display: none;
  }
`;

const DashboardLogoCorner = styled.div`
  display: none;

  @media (min-width: 1025px) {
    display: flex;
    align-items: center;
    justify-content: center;
    position: fixed;
    top: 0;
    left: 0;
    width: ${props => (props.$isExpanded ? '250px' : '70px')};
    z-index: 1001;
    padding: 1.25rem 0.375rem;
    box-sizing: border-box;
    min-height: 60px;
    background: linear-gradient(180deg, rgba(15, 15, 15, 0.98) 0%, rgba(20, 20, 20, 0.95) 100%);
    border-bottom: 1px solid var(--primary-rgba-15);
    border-right: 1px solid var(--primary-rgba-15);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.35);
    transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }
`;

const CornerLogoImage = styled.img`
  display: block;
  width: auto;
  height: auto;
  max-width: 100%;
  max-height: ${props => (props.$isExpanded ? '52px' : '40px')};
  object-fit: contain;
  margin: 0;
  padding: 0;
`;

const MenuButton = styled(motion.button)`
  background: var(--primary-rgba-10);
  border: 1px solid transparent;
  color: rgba(255, 255, 255, 0.95);
  font-size: 1.25rem;
  cursor: pointer;
  padding: 0.5rem;
  display: none; /* Hidden on desktop */
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  border-radius: 10px;
  position: relative;
  z-index: 3;
  flex-shrink: 0;
  box-shadow: 0 4px 12px var(--primary-rgba-10),
              inset 0 1px 0 rgba(255, 255, 255, 0.1);
  overflow: hidden;

  /* Shine effect */
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg,
      transparent 0%,
      var(--primary-rgba-20) 50%,
      transparent 100%
    );
    transform: translateX(-100%);
    transition: transform 0.6s ease;
  }

  &:hover {
    background: var(--primary-rgba-15);
    transform: scale(1.08) rotate(5deg);
    box-shadow: 0 6px 20px var(--primary-rgba-20),
                inset 0 1px 0 rgba(255, 255, 255, 0.15);
    border-color: var(--primary-rgba-30);

    &::after {
      transform: translateX(100%);
    }
  }

  &:active {
    transform: scale(0.95);
  }
  
  /* Show on tablet and mobile */
  @media (max-width: 1024px) {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  /* Mobile: enlarge to a comfortable 48×48px touch target */
  @media (max-width: 768px) {
    font-size: 1.35rem;
    padding: 0.75rem;
    min-width: 3rem;   /* 48px */
    min-height: 3rem;  /* 48px */
    border-radius: 12px;
  }
`;

const SearchButton = styled(motion.button)`
  background: var(--primary-rgba-10);
  border: 1px solid transparent;
  color: rgba(255, 255, 255, 0.95);
  font-size: 1.1rem;
  cursor: pointer;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  border-radius: 10px;
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  z-index: 3;
  flex-shrink: 0;
  box-shadow: 0 4px 12px var(--primary-rgba-10),
              inset 0 1px 0 rgba(255, 255, 255, 0.1);
  overflow: hidden;

  /* Shine effect */
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg,
      transparent 0%,
      var(--primary-rgba-20) 50%,
      transparent 100%
    );
    transform: translateX(-100%);
    transition: transform 0.6s ease;
  }

  svg {
    position: relative;
    z-index: 1;
    transition: all 0.3s ease;
  }

  &:hover {
    background: var(--primary-rgba-15);
    transform: scale(1.1);
    box-shadow: 0 6px 20px var(--primary-rgba-20),
                inset 0 1px 0 rgba(255, 255, 255, 0.15);
    border-color: var(--primary-rgba-30);

    svg {
      color: var(--primary-color);
      transform: scale(1.1);
    }

    &::after {
      transform: translateX(100%);
    }
  }

  &:active {
    transform: scale(0.95);
  }
  
  @media (max-width: 1024px) {
    font-size: 1rem;
    padding: 0.45rem;
  }
  
  @media (max-width: 768px) {
    font-size: 0.95rem;
    padding: 0.4rem;
  }
  
  @media (max-width: 375px) {
    font-size: 0.9rem;
    padding: 0.35rem;
  }
`;


const NavRight = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-shrink: 0;
  flex-wrap: nowrap;
  overflow: visible;
  z-index: 1;

  /* Hide scrollbar */
  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
  
  @media (max-width: 1024px) {
    gap: 0.5rem;
  }
  
  @media (max-width: 768px) {
    gap: 0.4rem;
    flex: 1;
    justify-content: flex-end;
  }
  
  @media (max-width: 375px) {
    gap: 0.25rem;
  }
`;

const IconButton = styled(motion.button)`
  background: var(--primary-rgba-10);
  border: 1px solid transparent;
  color: rgba(255, 255, 255, 0.95);
  font-size: 1.1rem;
  cursor: pointer;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  border-radius: 10px;
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  z-index: 3;
  flex-shrink: 0;
  box-shadow: 0 4px 12px var(--primary-rgba-10),
              inset 0 1px 0 rgba(255, 255, 255, 0.1);
  overflow: hidden;

  /* Shine effect */
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg,
      transparent 0%,
      var(--primary-rgba-20) 50%,
      transparent 100%
    );
    transform: translateX(-100%);
    transition: transform 0.6s ease;
    z-index: 0;
  }

  svg {
    position: relative;
    z-index: 1;
    transition: all 0.3s ease;
  }

  &:hover {
    background: var(--primary-rgba-15);
    transform: scale(1.1);
    box-shadow: 0 6px 20px var(--primary-rgba-20),
                inset 0 1px 0 rgba(255, 255, 255, 0.15);
    border-color: var(--primary-rgba-30);

    svg {
      color: var(--primary-color);
      transform: scale(1.1);
    }

    &::before {
      transform: translateX(100%);
    }
  }

  &:active {
    transform: scale(0.95);
  }
  
  @media (max-width: 1024px) {
    font-size: 1rem;
    padding: 0.45rem;
  }
  
  @media (max-width: 768px) {
    font-size: 0.95rem;
    padding: 0.4rem;
  }
  
  @media (max-width: 375px) {
    font-size: 0.9rem;
    padding: 0.35rem;
  }
  
  /* Hide specific buttons on smaller screens */
  &.hide-on-mobile {
    @media (max-width: 768px) {
      display: none;
    }
  }
  
  &.hide-on-small-mobile {
    @media (max-width: 500px) {
      display: none;
    }
  }
`;

const NotificationBadge = styled.span`
  position: absolute;
  top: 0;
  right: 0;
  background: linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%);
  color: white;
  font-size: 0.7rem;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  box-shadow: 0 4px 12px rgba(220, 53, 69, 0.4),
              0 0 0 2px rgba(15, 15, 15, 0.95),
              inset 0 1px 0 rgba(255, 255, 255, 0.3);
  animation: pulse 2s ease-in-out infinite;
  z-index: 2;

  @keyframes pulse {
    0%, 100% {
      transform: scale(1);
      box-shadow: 0 4px 12px rgba(220, 53, 69, 0.4),
                  0 0 0 2px rgba(15, 15, 15, 0.95),
                  inset 0 1px 0 rgba(255, 255, 255, 0.3);
    }
    50% {
      transform: scale(1.1);
      box-shadow: 0 6px 16px rgba(220, 53, 69, 0.6),
                  0 0 0 2px rgba(15, 15, 15, 0.95),
                  inset 0 1px 0 rgba(255, 255, 255, 0.4);
    }
  }
  
  @media (max-width: 768px) {
    width: 14px;
    height: 14px;
    font-size: 0.65rem;
  }
  
  @media (max-width: 375px) {
    width: 12px;
    height: 12px;
    font-size: 0.6rem;
  }
`;


const Navbar = ({
  onMenuClick,
  user,
  isSearchVisible,
  setIsSearchVisible,
  searchQuery,
  setSearchQuery,
  isSidebarExpanded,
  isMobileSidebarOpen = false,
}) => {
  const { gradientEnabled, toggleGradient, theme: themeContext } = useContext(ThemeContext);
  const { currentUser } = useContext(AuthContext);
  const { t } = useTranslation();
  
  // Get the actual theme object
  const theme = themeContext || {};

  const showBrandWordmark = isSidebarExpanded || isMobileSidebarOpen;
  
  return (
    <>
      <DashboardLogoCorner
        $isExpanded={isSidebarExpanded}
        {...(showBrandWordmark ? { 'aria-hidden': true } : {})}
      >
        <CornerLogoImage
          src={BRAND_LOGO_SRC}
          alt={showBrandWordmark ? '' : 'TheRest360'}
          $isExpanded={isSidebarExpanded}
        />
      </DashboardLogoCorner>
    <NavbarContainer
      initial={{ y: -60 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.3 }}
      isSearchVisible={isSearchVisible}
      isSidebarExpanded={isSidebarExpanded}
      theme={theme}
    >
      <NavLeft>
        <MenuButton
          data-menu-toggle="true"
          onClick={onMenuClick}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
        >
          <FaBars />
        </MenuButton>
        <NavBrandGroup $withWordmark={showBrandWordmark}>
          {showBrandWordmark ? (
            <NavBrandWordmark>TheResto360</NavBrandWordmark>
          ) : null}
          <LogoImage
            src={BRAND_LOGO_SRC}
            alt={showBrandWordmark ? '' : 'TheRest360'}
            $withWordmark={showBrandWordmark}
          />
        </NavBrandGroup>
      </NavLeft>
      
      <NavRight>
        {!isSearchVisible && (
          <SearchButton
            onClick={() => setIsSearchVisible(true)}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            title={t('common.search')}
          >
            <FaSearch />
          </SearchButton>
        )}
        
        <LanguageSwitcher />
        
        <RestaurantDropdown currentUser={currentUser} />
        
        <QuickThemeSwitcher />
        
        <IconButton
          onClick={toggleGradient}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          title={gradientEnabled ? t('settings.turnOffGradient') : t('settings.turnOnGradient')}
          className="hide-on-mobile"
        >
          {gradientEnabled ? <FaEyeSlash /> : <FaPalette />}
        </IconButton>
        
        <IconButton
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          title={t('common.notifications')}
        >
          <FaBell />
          <NotificationBadge>3</NotificationBadge>
        </IconButton>
      </NavRight>
    </NavbarContainer>
    </>
  );
};

export default Navbar;